// Content-script-side document text extraction.
//
// pdfjs / mammoth / Whisper cannot run in the content-script realm:
// web.whatsapp.com's CSP blocks worker creation and dynamic script imports
// they need. So we delegate extraction to the offscreen document, which runs
// in chrome-extension origin and has its own permissive CSP. See
// src/offscreen.ts for the implementations (pdfjs / mammoth / @huggingface/
// transformers Whisper).
//
// Wire: content-script → background SW (creates offscreen if needed and
// forwards) → offscreen (runs the extractor) → response back up the same chain.

// Audio transcription runs Whisper-small (fp32 enc + q4 dec ≈ 135MB) in
// offscreen. First call has to download the model from HuggingFace; on a
// slow connection that can take 1-3 minutes. Generous timeout so the
// content-script doesn't bail and trigger a duplicate download.
const TEXT_EXTRACTION_TIMEOUT_MS = 20000;
const AUDIO_EXTRACTION_TIMEOUT_MS = 5 * 60 * 1000;

const DOCX_MIMETYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const PPTX_MIMETYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

import type { TranscriptionResult } from '@bt-common/interfaces/speech-contracts.interface';

export interface ExtractionResult {
  text: string | null;
  /** ISO-639 language code Whisper detected (audio only). null otherwise. */
  language?: string | null;
  /** Structured transcription result (audio only) — the authoritative
   *  bt-common speech-contract payload: flags, model, timings, source.
   *  `text`/`language` above stay populated for older consumers. */
  transcription?: TranscriptionResult | null;
}

/** Ambient sourceLanguage used by audio extractions when the caller
 *  (usually `waGetMessageMediaTool`, whose signature we don't want to
 *  extend to keep MCP schema stable) doesn't pass it explicitly. Set
 *  by `transcribeMessageThunk` right before it kicks off the tool
 *  call; cleared implicitly on the next set. Only meaningful within
 *  the content-script origin's JS context.
 *
 *  `origin` records HOW the language was resolved — explicit user pick
 *  ('user') vs per-chat sticky memory ('sticky') — and rides the same
 *  EXTRACT_DOC payload into the structured result's `languageSource`. */
let ambientSourceLanguage: string | undefined = undefined;
let ambientSourceLanguageOrigin: 'user' | 'sticky' | undefined = undefined;
export function setAmbientTranscribeSourceLanguage(
  lang: string | undefined,
  origin?: 'user' | 'sticky',
): void {
  ambientSourceLanguage = lang && lang !== 'auto' ? lang : undefined;
  ambientSourceLanguageOrigin = ambientSourceLanguage ? origin : undefined;
}

/** Uint8Array → base64, chunked. The naive per-byte string concat this
 *  replaces is quadratic-ish on large buffers and blocks the content-script
 *  main thread for seconds on multi-MB voice notes. 32KB chunks keep each
 *  String.fromCharCode.apply under the engine's argument limit. */
function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  const parts: string[] = [];
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const sub = bytes.subarray(i, i + CHUNK);
    parts.push(String.fromCharCode.apply(null, sub as unknown as number[]));
  }
  return btoa(parts.join(''));
}

export async function extractPdfTextInContentScript(
  bytes: Uint8Array,
  mimetype: string,
  options: { maxChars?: number; maxPages?: number; sourceLanguage?: string } = {},
): Promise<ExtractionResult> {
  const maxChars = options.maxChars ?? 8000;
  if (!bytes || !mimetype) return { text: null };

  // text/* runs entirely inline — no need to round-trip through offscreen.
  if (mimetype.startsWith('text/')) {
    try {
      return { text: new TextDecoder('utf-8').decode(bytes).slice(0, maxChars) };
    } catch {
      return { text: null };
    }
  }

  const isPdf = mimetype === 'application/pdf';
  const isDocx = mimetype === DOCX_MIMETYPE;
  const isPptx = mimetype === PPTX_MIMETYPE;
  const isAudio = mimetype.startsWith('audio/');
  if (!isPdf && !isDocx && !isPptx && !isAudio) return { text: null };

  try {
    const dataBase64 = bytesToBase64(bytes);

    const timeoutMs = isAudio ? AUDIO_EXTRACTION_TIMEOUT_MS : TEXT_EXTRACTION_TIMEOUT_MS;
    // `sourceLanguage` piggy-backs on the same EXTRACT_DOC message so
    // the offscreen document gets the caller's language preference
    // directly, without any chrome.storage.local round-trip. Only
    // meaningful for audio; text extractors ignore it. Explicit option
    // wins; otherwise we fall back to the module-level ambient value
    // set by transcribeMessageThunk right before it triggers this
    // pipeline.
    const effectiveSourceLanguage = options.sourceLanguage ?? ambientSourceLanguage;
    if (isAudio) {
      console.log(`[doc-extraction] audio EXTRACT_DOC sourceLanguage=${effectiveSourceLanguage ?? 'undefined'}`);
    }
    const resp: any = await Promise.race([
      chrome.runtime.sendMessage({
        type: 'EXTRACT_DOC',
        payload: {
          dataBase64,
          mimetype,
          maxChars,
          maxPages: options.maxPages,
          sourceLanguage: effectiveSourceLanguage,
          sourceLanguageOrigin: options.sourceLanguage ? undefined : ambientSourceLanguageOrigin,
        },
      }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('EXTRACT_DOC timeout')), timeoutMs)),
    ]);

    if (!resp?.success) {
      console.warn('[doc-extraction] offscreen extraction failed', resp?.error);
      return { text: null };
    }
    return {
      text: resp.text ?? null,
      language: resp.language ?? null,
      transcription: resp.transcription ?? null,
    };
  } catch (e) {
    console.warn('[doc-extraction] sendMessage failed', e);
    return { text: null };
  }
}
